import { useTranslation } from "react-i18next"
import { useSuspenseQueryGet } from "../../../../../typed-client/hooks/use-suspense-query-get"
import { IconCheckBox } from "../../../-components/icon-check-box"
import { getFormattedDate } from "../../../../../utils/get-formatted-date"
import { FieldData } from "../../../../../-components/field-data"
import { ProductionDto, FieldData as TFieldData } from "@/orval/gen"
import { TheasoftColumnDef } from "@/routes/-components/custom-tanstack-table"
import { VisibilityState } from "@tanstack/react-table"

type ProductionTableColumns = {
  baseColumns: TheasoftColumnDef<ProductionDto>[]
  extendedColumns: TheasoftColumnDef<ProductionDto>[]
  defaultColumnVisibility: VisibilityState
}

export function useProductionTableColumns(): ProductionTableColumns {
  const { t } = useTranslation("app_server")

  const { data: productionFieldTypes } = useSuspenseQueryGet(
    "/api/Production/GetProductionFieldTypesForTable",
    null
  )
  const baseColumns = React.useMemo<TheasoftColumnDef<ProductionDto>[]>(
    () => [
      {
        accessorKey: "title",
        header: t("Title"),
      },
      { accessorKey: "originalTitle", header: t("OriginalTitle") },
      {
        accessorKey: "genre",
        header: t("Genre"),
      },
      {
        accessorKey: "isInactiv",
        header: t("Played"),
        cell: ({ cell }) => <IconCheckBox checked={cell.getValue<boolean>()} />,
        enableSorting: false,
      },
      {
        accessorKey: "premiere",
        header: t("Premiere"),
        cell: ({ cell }) => getFormattedDate(cell.getValue<string>()),
      },
      { accessorKey: "stage", header: t("Stage") },
    ],
    []
  )

  const extendedColumns = React.useMemo<TheasoftColumnDef<ProductionDto>[]>(
    () => [
      { accessorKey: "id", header: "Id" },
      ...baseColumns,
      {
        accessorKey: "work",
        header: t("Work"),
      },
      ...productionFieldTypes.map<TheasoftColumnDef<ProductionDto>>(
        (productionFieldType) => ({
          accessorFn: (row) =>
            row.productionFields?.find(
              (productionField) =>
                productionField?.type.id ===
                productionFieldType.productionFieldTypeId
            ),
          header: productionFieldType.name,
          cell: ({ cell }) => {
            const cellValue = cell.getValue<TFieldData | undefined>()
            return cellValue && <FieldData fieldData={cellValue} />
          },
          enableSorting: false,
          id: "productionField-" + productionFieldType.productionFieldTypeId,
        })
      ),
    ],
    []
  )

  const defaultColumnVisibility = React.useMemo<VisibilityState>(
    () => ({
      id: false,
      work: false,
      ...productionFieldTypes.reduce(
        (acc, v) => {
          acc[`productionField-${v.productionFieldTypeId}`] = false
          return acc
        },
        {} as Record<string, boolean>
      ),
    }),
    [productionFieldTypes]
  )

  return { baseColumns, extendedColumns, defaultColumnVisibility }
}

